import { gql, useLazyQuery } from '@apollo/client';
import { Dialog, Text, Tooltip } from '@radix-ui/themes';
import { Briefcase, Loader, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDebounce } from '../../hooks/useDebounce';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import ExternalJobRef from '../elements/ExternalJobRef';

const SEARCH_QUERY = gql`
  query ($query: String!, $type: SearchCategories!) {
    search(input: { query: $query, type: $type }) {
      ... on Job {
        jobId: id
        title
        location {
          name
        }
        externalRef {
          externalId
          source
        }
      }

      ... on Candidate {
        id
        firstName
        lastName
        email
        applications {
          nodes {
            id
            score
            job {
              title
              threshold
            }
          }
        }
      }
    }
  }
`;

type Suggestion =
  | {
      __typename: 'Candidate';
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      applications: {
        nodes: {
          id: string;
          score: number;
          job: {
            title: string;
            threshold: number;
          };
        }[];
      };
    }
  | {
      __typename: 'Job';
      jobId: string;
      title: string;
      location: {
        name: string;
      };
      externalRef: {
        externalId: string;
        source: string;
      } | null;
    };

const Candidate = ({
  candidate,
  onSelect,
  setOpen,
}: {
  candidate: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    applications: { nodes: { id: string; score: number; job: { title: string; threshold: number } }[] };
  };
  onSelect: () => void;
  setOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const navigateToCand = () => {
    navigate('/candidates/' + candidate.id);
    onSelect();
    setOpen(false);
  };

  return (
    <div
      className="m-0 p-2 flex clickable justify-between items-center border-t list-menu-item"
      onClick={() => navigateToCand()}
    >
      <div className="m-0 p-0">
        {candidate.firstName} {candidate.lastName}
        <div className="m-0 p-0 font-light">{candidate.email}</div>
      </div>
      <div className="m-0 p-0">
        {candidate.applications.nodes.map((app) => {
          return (
            <Tooltip content={<Text size="3">{`${app.job.title}`}</Text>} key={`tooltip-name-${app.id}`}>
              <div
                className={'name-pill float-right ' + (app.score >= app.job.threshold ? 'purple' : 'grey')}
                key={app.id}
              >
                {app.score ? Math.floor(app.score) : '-'}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const Job = ({
  job,
  onSelect,
  setOpen,
}: {
  job: {
    jobId: string;
    title: string;
    location: { name: string };
    externalRef: { externalId: string; source: string } | null;
  };
  onSelect: () => void;
  setOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const navigateToJob = () => {
    navigate(`/job/${job.jobId}`);
    onSelect();
    setOpen(false);
  };

  return (
    <div
      className="m-0 p-2 flex clickable justify-between items-center border-t list-menu-item"
      onClick={() => navigateToJob()}
    >
      <div className="m-0 p-0">
        {job.title}
        <div className="m-0 p-0 font-light">{job.location.name}</div>
        {job.externalRef?.externalId && (
          // The row navigates on click, so keep the copy button's click from
          // reaching it — otherwise copying the id also opens the job.
          <div onClick={(e) => e.stopPropagation()}>
            <ExternalJobRef externalId={job.externalRef.externalId} source={job.externalRef.source} />
          </div>
        )}
      </div>
      <div className="p-0 m-0">
        <div className="job-result float-right">
          <Briefcase />
        </div>
      </div>
    </div>
  );
};

const Searchbar = () => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchbarRef = useRef<HTMLDivElement & HTMLInputElement>(null);

  const [searchCategory, setSearchCategory] = useState('ALL');
  const [search, { loading, data }] = useLazyQuery(SEARCH_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [suggestion, setSuggestion] = useState<Suggestion[]>([]);

  useEffect(() => {
    if (searchValue === '') setSuggestion([]);
    else if (data) setSuggestion(data.search);
  }, [data, searchValue]);

  // Search analytics: lengths and counts only, never the search text (it often contains names).
  const searchesInDialogRef = useRef(0);
  const lastResultsCountRef = useRef(0);
  const resultOpenedRef = useRef(false);
  const lastTrackedDataRef = useRef<unknown>(null);
  useEffect(() => {
    if (!data || loading || searchValue === '' || data === lastTrackedDataRef.current) return;
    lastTrackedDataRef.current = data;
    searchesInDialogRef.current += 1;
    lastResultsCountRef.current = data.search?.length ?? 0;
    track(AnalyticsEvents.SEARCH_PERFORMED, {
      category: searchCategory.toLowerCase(),
      query_length: searchValue.length,
      results_count: lastResultsCountRef.current,
    });
  }, [data, loading, searchValue, searchCategory]);

  const onResultSelected = (resultType: 'candidate' | 'job', position: number) => {
    resultOpenedRef.current = true;
    track(AnalyticsEvents.SEARCH_RESULT_OPENED, {
      category: searchCategory.toLowerCase(),
      result_type: resultType,
      position,
    });
    setOpen(false);
  };

  const onOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      searchesInDialogRef.current = 0;
      resultOpenedRef.current = false;
    } else if (searchesInDialogRef.current > 0 && !resultOpenedRef.current) {
      track(AnalyticsEvents.SEARCH_ABANDONED, {
        searches: searchesInDialogRef.current,
        last_results_count: lastResultsCountRef.current,
      });
    }
    setOpen(nextOpen);
  };

  useEffect(() => {
    if (searchValue.length > 0) {
      setIsLoading(true);
      search({ variables: { query: searchValue, type: searchCategory } });
    }
  }, [searchValue, searchCategory]);

  const debounceSearch = useDebounce((s) => setSearchValue(s), 500);

  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
      case 'JOB':
        return t('nav.searchbar.categories.JOB');
      case 'CANDIDATE':
        return t('nav.searchbar.categories.CANDIDATE');
      default:
        return t('nav.searchbar.categories.ALL');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger>
        <button
          className="search-trigger-btn hidden md:flex items-center justify-between text-black bg-white border border-gray-300 rounded px-4 py-1 cursor-pointer"
          style={{ width: '200px' }}
          aria-label={t('nav.searchbar.placeholder')}
        >
          <span>{t('nav.searchbar.placeholder')}</span>
          <Search size={14} />
        </button>
      </Dialog.Trigger>
      <Dialog.Trigger>
        <button
          className="search-trigger-btn md:hidden bg-transparent border-0 text-white p-1 cursor-pointer"
          aria-label={t('nav.searchbar.placeholder')}
        >
          <Search size={22} />
        </button>
      </Dialog.Trigger>

      <Dialog.Content
        style={{ maxWidth: 600, padding: '1rem', backgroundColor: 'var(--lighter-gray)', overflow: 'visible' }}
      >
        <Dialog.Title className="sr-only" style={{ display: 'none' }}>
          {t('nav.searchbar.placeholder')}
        </Dialog.Title>
        <div ref={searchbarRef} className="search-bar-container w-full">
          <div className="search-bar-wrapper border bg-white" style={{ height: '45px', overflow: 'visible' }}>
            <div ref={dropdownRef} className="search-bar-select h-full" style={{ position: 'relative' }}>
              <a
                className="btn-dropdown-link h-full flex items-center justify-center"
                onClick={(e) => {
                  e.preventDefault();
                  setShowDropdown(!showDropdown);
                }}
                href="#"
              >
                <span>{getCategoryLabel(searchCategory)}</span>
              </a>
              <div
                className={`btn-dropdown-options ${showDropdown ? 'btn-dropdown-options-visible' : ''}`}
                style={{ top: '100%', zIndex: 9999, position: 'absolute' }}
              >
                {showDropdown && (
                  <ul>
                    <li
                      onClick={() => {
                        setSearchCategory('ALL');
                        setShowDropdown(false);
                      }}
                    >
                      <span>{t('nav.searchbar.categories.ALL')}</span>
                    </li>
                    <li
                      onClick={() => {
                        setSearchCategory('JOB');
                        setShowDropdown(false);
                      }}
                    >
                      <span>{t('nav.searchbar.categories.JOB')}</span>
                    </li>
                    <li
                      onClick={() => {
                        setSearchCategory('CANDIDATE');
                        setShowDropdown(false);
                      }}
                    >
                      <span>{t('nav.searchbar.categories.CANDIDATE')}</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
            <div className="vertical-divider" style={{ height: '60%', width: '1px', backgroundColor: '#e0e0e0' }}></div>
            <input
              autoFocus
              onChange={(e) => debounceSearch(e.target.value.trim())}
              className="search-bar-input px-4 h-full"
              type="text"
              placeholder={t('nav.searchbar.placeholder')}
              aria-label={t('nav.searchbar.placeholder')}
            />
          </div>
          {searchValue.length > 0 && (
            <div className="search-results relative mt-2 shadow-sm border" style={{ borderRadius: '8px' }}>
              {!isLoading && data && suggestion.length === 0 && (
                <span className="w-full flex justify-center p-4 text-gray-500">{t('nav.searchbar.noResults')}</span>
              )}
              {suggestion &&
                suggestion.map((res, index) => {
                  if (res.__typename === 'Candidate') {
                    return (
                      <Candidate
                        key={index}
                        candidate={res}
                        onSelect={() => onResultSelected('candidate', index + 1)}
                        setOpen={setOpen}
                      />
                    );
                  } else if (res.__typename === 'Job') {
                    return (
                      <Job
                        key={index}
                        job={res}
                        onSelect={() => onResultSelected('job', index + 1)}
                        setOpen={setOpen}
                      />
                    );
                  }
                })}
              {isLoading && <Loader className="color dark-grey block mx-auto animate-spin my-4" />}
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default Searchbar;
